<script setup lang="ts">
import type { SubItem } from '@/types/types'
import type { Icons } from '@/components/base/BaseIcon.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    menuName: string
    isDropdownOpen: boolean
    icon: Icons
    isMenuActive: boolean
    subitems: SubItem[]
  }>(),
  {
    isDropdownOpen: false,
    isMenuActive: false
  }
)

const emit = defineEmits<{
  (e: 'toggleDropdown'): void
}>()

// Route matching helpers — must be declared first
const hasMatchingQuery = (query: SubItem['query']) => {
  if (!query) return true
  return Object.keys(query).every((key) => route.query[key] === query[key])
}

const hasMatchingMatchQuery = (query: NonNullable<SubItem['matchRoutes']>[number]['query']) => {
  if (!query) return true
  return Object.keys(query).every((key) =>
    query[key] === null ? route.query[key] === undefined : route.query[key] === query[key]
  )
}

const hasMatchingParams = (params: SubItem['params']) => {
  if (!params) return true
  return Object.keys(params).every((key) => route.params[key] === params[key])
}

type ResolvedTo = { name: string; params?: SubItem['params']; query?: SubItem['query'] }

const resolveTo = (item: SubItem, parent?: SubItem): ResolvedTo | null => {
  const name = item.name ?? parent?.name
  if (!name) return null

  return {
    name,
    params: { ...(parent?.params ?? {}), ...(item.params ?? {}) },
    query: { ...(parent?.query ?? {}), ...(item.query ?? {}) }
  }
}

const isResolvedItemActive = (to: ResolvedTo | null) => {
  if (!to) return false
  return route.name === to.name && hasMatchingParams(to.params) && hasMatchingQuery(to.query)
}

const isItemActive = (item: SubItem, parent?: SubItem) => {
  if (item.excludeFromActive) return false

  const to = resolveTo(item, parent)
  if (isResolvedItemActive(to)) return true
  if (item.matchRoutes?.some((match) => route.name === match.name && hasMatchingMatchQuery(match.query))) {
    return true
  }
  if (item.matchParams?.length && to) {
    return item.matchParams.some((params) => route.name === to.name && hasMatchingParams(params))
  }
  return false
}

const hasActiveDescendant = (items: SubItem[], parent?: SubItem): boolean => {
  return items.some((item) => {
    if (isItemActive(item, parent)) return true
    if (item.subItems?.length) return hasActiveDescendant(item.subItems, { ...parent, ...item })
    return false
  })
}

const resolvedSubitems = computed(() =>
  props.subitems.map((item) => ({
    item,
    to: resolveTo(item),
    children: (item.subItems ?? []).map((subItem) => ({ subItem, to: resolveTo(subItem, item) }))
  }))
)

const subitemsTranslations = computed(() => {
  return props.subitems.map((item) => {
    return item.translationKey ?? (item.name ? item.name.charAt(0).toLowerCase() + item.name.slice(1) : '')
  })
})

const activeClass = 'text-nav-active-text! bg-nav-active-bg font-medium rounded-lg hover:bg-nav-active-hover!'

const hasSubrouteActive = computed(() => {
  return hasActiveDescendant(props.subitems)
})

const isSectionActive = computed(() => hasSubrouteActive.value || props.isMenuActive)

const iconName = computed<Icons>(() =>
  isSectionActive.value ? (props.icon.replace(/IconOutline$/, 'IconSolid24') as Icons) : props.icon
)

const isGroupOpen = (item: SubItem) => {
  if (!item.subItems?.length) return false
  return hasActiveDescendant(item.subItems, item)
}

const openFirstItem = (item: SubItem) => {
  if (item.subItems?.length) {
    router.push({ name: item.subItems[0].name, params: item.subItems[0].params, query: item.subItems[0].query })
  }
}
</script>

<template>
  <div>
    <button
      type="button"
      class="cursor-pointer relative flex items-center px-3 py-1.5 text-sm text-foreground transition-colors duration-200 ease-out hover:rounded-lg hover:bg-nav-hover"
      :class="[isSectionActive && 'font-medium text-nav-active-text', isDropdownOpen && 'rounded-lg', 'w-full']"
      @click="emit('toggleDropdown')"
    >
      <BaseIcon :name="iconName" class="h-5 w-5 text-primary" />
      <div class="mx-3 flex-1 text-left">
        <span>{{ menuName }}</span>
      </div>

      <div :class="[isDropdownOpen ? 'rotate-0' : '-rotate-90', 'transition-[transform] duration-300']">
        <BaseIcon name="HeroChevronDownIconSolid" class="size-4 text-foreground-disabled" />
      </div>
    </button>

    <div
      class="grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out"
      :class="isDropdownOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <div class="min-h-0 overflow-hidden" :inert="!isDropdownOpen">
        <div class="space-y-1">
          <template v-for="({ item, to, children }, index) in resolvedSubitems" :key="item.name ?? item.text ?? index">
            <div>
              <!-- Level 2: either a leaf link, or a group header (that routes to its first child) -->
              <router-link
                v-if="!item.subItems?.length && to"
                :to="to"
                class="group flex items-center pl-4 pr-2 py-1.5 text-sm text-foreground-secondary transition-colors duration-200 ease-out hover:bg-nav-hover hover:rounded-lg"
                :class="{ [activeClass]: isItemActive(item) }"
              >
                <div class="mr-1 w-6 shrink-0"></div>
                <span v-if="item.text"> {{ item.text }} </span>
                <span v-else>
                  {{ t(`sidebar.${subitemsTranslations[index]}`) }}
                </span>
              </router-link>

              <button
                v-else
                type="button"
                class="group flex w-full items-center pl-4 pr-2 py-1.5 text-sm text-foreground-secondary transition-colors duration-200 ease-out hover:bg-nav-hover hover:rounded-lg text-left cursor-default"
                :class="{ [activeClass]: isGroupOpen(item) }"
                @click="openFirstItem(item)"
              >
                <div class="mr-1 w-6 shrink-0"></div>
                <div class="flex-1 text-left">
                  <span>{{ item.text }}</span>
                </div>

                <div
                  :class="[isGroupOpen(item) ? 'rotate-0' : '-rotate-90', 'mr-2 transition-[transform] duration-300']"
                >
                  <BaseIcon name="HeroChevronDownIconSolid" class="size-4 text-foreground-disabled" />
                </div>
              </button>

              <!-- Level 3: only show when any child is active -->
              <div v-if="item.subItems?.length && isGroupOpen(item)" class="space-y-1 mt-1">
                <template
                  v-for="({ subItem, to: subTo }, subIndex) in children"
                  :key="subItem.name ?? subItem.text ?? subIndex"
                >
                  <router-link
                    v-if="subTo"
                    :to="subTo"
                    class="group flex items-center pl-8 pr-2 py-1.5 text-sm text-foreground-secondary transition-colors duration-200 ease-out hover:bg-nav-hover hover:rounded-lg"
                    :class="{ [activeClass]: isItemActive(subItem, item) }"
                  >
                    <div class="mr-1 w-6 shrink-0"></div>
                    <span>{{ subItem.text }}</span>
                  </router-link>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
